import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellOff, Loader2, Smartphone, Home } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationToggle = ({ famigliaId, userId, nomeAssistito, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSBrowser, setIsIOSBrowser] = useState(false);
  const [storedPlayerId, setStoredPlayerId] = useState(null);
  const listenerRef = useRef(null);
  const hasRegisteredRef = useRef(false);

  // Detect iOS and whether running as installed PWA
  useEffect(() => {
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua);
    setIsIOS(isiOS);
    
    if (isiOS) {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone === true;
      setIsIOSBrowser(!isStandalone);
    }
  }, []);

  // Register player ID to backend
  const registerPlayerId = useCallback(async (playerId) => {
    if (!playerId || !famigliaId || hasRegisteredRef.current) return false;
    
    try {
      console.log('[Push] Registering player_id:', playerId.substring(0, 20) + '...');
      const response = await fetch(`${API_URL}/api/notifications/register-onesignal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          famiglia_id: famigliaId,
          player_id: playerId,
          user_id: userId,
          platform: isIOS ? 'ios' : 'web'
        })
      });

      if (response.ok) {
        hasRegisteredRef.current = true;
        setStoredPlayerId(playerId);
        setIsActive(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[Push] Register error:', err);
      return false;
    }
  }, [famigliaId, userId, isIOS]);

  // Check backend status and sync if needed
  const checkStatus = useCallback(async () => {
    setLoading(true);
    hasRegisteredRef.current = false;
    
    try {
      if (!famigliaId) {
        setLoading(false);
        return;
      }

      // Get backend status
      const res = await fetch(`${API_URL}/api/user/${famigliaId}/push-status`);
      if (!res.ok) {
        setLoading(false);
        return;
      }
      
      const data = await res.json();
      const backendEnabled = data.push_enabled === true || data.onesignal_enabled === true;
      const backendPlayerId = data.onesignal_player_id || null;
      setStoredPlayerId(backendPlayerId);

      // Check local OneSignal subscription
      let localOptedIn = false;
      let localPlayerId = null;
      
      try {
        if (window.OneSignal?.User?.PushSubscription) {
          localOptedIn = await window.OneSignal.User.PushSubscription.optedIn;
          localPlayerId = await window.OneSignal.User.PushSubscription.id;
        }
      } catch (e) {
        console.log('[Push] OneSignal not available:', e.message);
      }

      // Sync if local is opted in with a different ID than stored
      if (localOptedIn && localPlayerId && localPlayerId !== backendPlayerId) {
        console.log('[Push] Syncing new player_id to backend');
        await registerPlayerId(localPlayerId);
      }

      // Active only if backend says enabled AND local is opted in
      const shouldBeActive = backendEnabled && localOptedIn && !!localPlayerId;
      setIsActive(shouldBeActive);

    } catch (err) {
      console.error('[Push] Status error:', err);
    } finally {
      setLoading(false);
    }
  }, [famigliaId, registerPlayerId]);

  // Setup subscription change listener
  useEffect(() => {
    const setupListener = async () => {
      try {
        if (!window.OneSignal?.User?.PushSubscription) return;

        // Define the change handler
        const handleChange = async (event) => {
          console.log('[Push] Subscription changed:', event);
          
          const optedIn = event?.current?.optedIn;
          const id = event?.current?.id;
          
          if (optedIn && id) {
            const success = await registerPlayerId(id);
            if (success && enabling) {
              toast.success('Notifiche attivate!');
              setEnabling(false);
            }
          }
        };

        // Store ref for cleanup
        listenerRef.current = handleChange;
        
        // Add the listener
        window.OneSignal.User.PushSubscription.addEventListener('change', handleChange);
        console.log('[Push] Subscription listener registered');

      } catch (e) {
        console.log('[Push] Could not setup listener:', e.message);
      }
    };

    setupListener();

    // Cleanup on unmount
    return () => {
      try {
        if (listenerRef.current && window.OneSignal?.User?.PushSubscription) {
          window.OneSignal.User.PushSubscription.removeEventListener('change', listenerRef.current);
          console.log('[Push] Subscription listener removed');
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [registerPlayerId, enabling]);

  // Check status on mount and when famigliaId changes
  useEffect(() => {
    checkStatus();
  }, [famigliaId, checkStatus]);

  const handleEnable = async () => {
    setEnabling(true);
    hasRegisteredRef.current = false;
    
    try {
      // Check OneSignal availability
      if (!window.OneSignal?.Notifications) {
        toast.error('Ricarica la pagina e riprova');
        setEnabling(false);
        return;
      }

      // Request permission
      await window.OneSignal.Notifications.requestPermission();
      
      // Check if permission was granted
      const permission = await window.OneSignal.Notifications.permission;
      if (!permission) {
        toast.error('Permesso non concesso', {
          description: 'Abilita le notifiche nelle impostazioni'
        });
        setEnabling(false);
        return;
      }

      // Opt in to push subscription
      await window.OneSignal.User.PushSubscription.optIn();

      // Check if already subscribed immediately after optIn
      const optedIn = await window.OneSignal.User.PushSubscription.optedIn;
      const playerId = await window.OneSignal.User.PushSubscription.id;
      
      if (optedIn && playerId) {
        // Register immediately without waiting for event
        const success = await registerPlayerId(playerId);
        if (success) {
          toast.success('Notifiche attivate!');
          setEnabling(false);
        }
        // If not successful, the change event listener will handle it
      }
      // If not yet opted in/no id, the change event listener will handle it
      
    } catch (err) {
      console.error('[Push] Enable error:', err);
      toast.error('Errore attivazione');
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    setEnabling(true);
    try {
      if (window.OneSignal?.User?.PushSubscription) {
        await window.OneSignal.User.PushSubscription.optOut().catch(() => {});
      }
      await fetch(`${API_URL}/api/notifications/unregister-onesignal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ famiglia_id: famigliaId })
      });
      setIsActive(false);
      setStoredPlayerId(null);
      hasRegisteredRef.current = false;
      toast.info('Notifiche disattivate');
    } catch (err) {
      console.error('[Push] Disable error:', err);
      toast.error('Errore');
    } finally {
      setEnabling(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">Caricamento...</span>
      </div>
    );
  }

  // iOS browser (not installed as PWA) - show install message instead of toggle
  if (isIOSBrowser) {
    return (
      <div className="flex items-center gap-4 p-4 rounded-xl border bg-amber-50 border-amber-200">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100">
          <Home className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-amber-800">
            Notifiche su iPhone
          </p>
          <p className="text-sm text-amber-600">
            Funzionano solo dall'app installata sulla schermata Home. Tocca "Condividi" → "Aggiungi a Home".
          </p>
        </div>
      </div>
    );
  }

  // Compact mode
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {isActive ? <Bell className="w-4 h-4 text-green-500" /> : <BellOff className="w-4 h-4 text-gray-400" />}
        <Switch 
          checked={isActive} 
          onCheckedChange={(c) => c ? handleEnable() : handleDisable()} 
          disabled={enabling} 
          className="data-[state=checked]:bg-green-500" 
        />
      </div>
    );
  }

  // Full mode
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border ${isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? 'bg-green-100' : 'bg-gray-200'}`}>
        {enabling ? <Loader2 className="w-5 h-5 animate-spin text-green-600" /> : isActive ? <Bell className="w-5 h-5 text-green-600" /> : <BellOff className="w-5 h-5 text-gray-500" />}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className={`font-semibold ${isActive ? 'text-green-800' : 'text-gray-700'}`}>
            {enabling ? 'Attivazione...' : isActive ? 'Notifiche Attive' : 'Notifiche Disattivate'}
          </p>
          {isIOS && <Smartphone className="w-4 h-4 text-gray-400" />}
        </div>
        <p className={`text-sm ${isActive ? 'text-green-600' : 'text-gray-500'}`}>
          {isActive ? `Aggiornamenti su ${nomeAssistito || 'il tuo assistito'}` : 'Attiva per ricevere notifiche'}
        </p>
      </div>
      <Switch
        checked={isActive}
        onCheckedChange={(c) => c ? handleEnable() : handleDisable()}
        disabled={enabling}
        className="data-[state=checked]:bg-green-500"
        data-testid="notification-toggle"
      />
    </div>
  );
};

export default NotificationToggle;
